import React, { JSX, useEffect, useState } from 'react';
import { Button } from 'antd';
import { Player } from '@/components/Player/Player';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { KeyPad } from '@/components/KeyPad/KeyPad';
import { TabGroup } from '@/components/TabGroup/TabGroup';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { Dynamics } from '@/services/dynamics/dynamics';
import { State } from '@/services/state.service';
import { DynRelatedToData } from '@/services/dynamics/interfaces/relate-to-info.interface';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { ESessionStatus } from '@/shared/enums/ringing-status.enum';
import transferIcon from '@/public/icons/transfer.svg';
import { CallPanel } from '@/components/CallPanel/CallPanel';
import {
  ESolgariCallType,
  EStatusCode,
} from '@/services/dynamics/interfaces/api/phone-call.interface';
import { ISession, SipJs } from '@/services/sip-js.service';
import { WrapUp } from '@/pages/WrapUp/WrapUp';
import { useWrapUpTimer } from '@/hooks/useWrapUpTimer';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import './Active.scss';

export type SubPanelType = 'note' | 'transfer' | 'keyPad' | 'groupCall';

export interface INoteState {
  text?: string;
  relatedToData?: DynRelatedToData;
  assignToData?: any;
  id?: string;
  extendedSubject?: string;
}

export const Active = observer(() => {
  const {
    callStore: {
      wrapUpSession,
      activeContact,
      callNote,
      setCallNote,
      setWrapUpSession,
    },
  } = useStore();
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [subPanel, setSubPanel] = useState<SubPanelType>(
    SipJs.conference ? 'groupCall' : 'note',
  );

  const { wrapUp, removeWrapUp: originalRemoveWrapUp } = useWrapUpTimer();

  const removeWrapUp = () => {
    if (Dynamics.getVersion() === 2) {
      State.setState({ callNoteId: null });
    }
    originalRemoveWrapUp();
  };

  const client: IEntity = sessions[0]?.remoteUser?.connectedUsers[0];
  const isMultiSession = (): boolean => sessions.length > 1;

  const subPanelHandler = (value: SubPanelType) => {
    setSubPanel(value);
  };

  const sendTone = (value: string) => {
    SipJs.sendTone(value);
  };

  const attendedTransfer = () => {
    const clientSession = sessions[0];
    const agentSession = sessions[1];
    const agentEntity = agentSession.remoteUser.connectedUsers[0] as IEntity;

    SipJs.attendedTransfer(clientSession.id, agentSession.id);

    const { isDialerActive } = State.state$.getValue();

    Dynamics.savePhoneCallNote(
      {
        desc: '',
        entity: clientSession.remoteUser.connectedUsers[0] as IEntity,
        timer: clientSession.timer,
        extendedSubject: `, transferred to Agent ${agentEntity.fullName}`,
        callState: EStatusCode.OPEN,
        callType: isDialerActive
          ? ESolgariCallType.DIALER
          : ESolgariCallType.REGULAR,
      },
      clientSession,
    );
  };

  const shouldShowKeyPad = (panel: SubPanelType): boolean => {
    if (
      panel !== 'keyPad' &&
      !(Dynamics.getVersion() === 2 && panel === 'note')
    )
      return false;

    if (isMultiSession()) return true;

    const user = sessions?.[0]?.remoteUser;
    return !!(sessions?.length && user && user.type !== 'external');
  };

  const renderSubPanel = (panel: SubPanelType): JSX.Element => {
    if (shouldShowKeyPad(panel)) {
      return <KeyPad onKeyHandle={sendTone} />;
    }

    if (wrapUp?.show) {
      return (
        <CallPanel
          savePhoneCallHandler={savePhoneCall}
          client={wrapUpSession?.remoteUser?.connectedUsers[0]}
        />
      );
    }

    switch (panel) {
      case 'transfer':
        return <TabGroup mode={subPanel} />;
      case 'keyPad':
        return <KeyPad onKeyHandle={sendTone} />;
      case 'groupCall':
        return <TabGroup mode={subPanel} />;
      case 'note':
      default:
        return (
          <CallPanel
            savePhoneCallHandler={savePhoneCall}
            client={
              wrapUp.show
                ? wrapUpSession?.remoteUser?.connectedUsers[0]
                : client
            }
          />
        );
    }
  };

  const renderPlayers = (): JSX.Element[] => {
    return sessions.map((session: ISession) => (
      <ErrorBoundary key={session.id}>
        <Player session={session} onSwitchSubPanel={subPanelHandler} />
      </ErrorBoundary>
    ));
  };

  const savePhoneCall = async (
    noteParam?: INoteState,
    currentSession?: ISession,
    assignedEntity?: IEntity,
  ): Promise<string> => {
    const noteToSave = noteParam ?? callNote;
    if (!noteToSave.id) {
      noteToSave.id = callNote.id;
    }
    const { text, relatedToData, id: noteId } = noteToSave;
    const session: ISession = wrapUpSession
      ? wrapUpSession
      : currentSession || sessions[0];

    if (SipJs.isMultiSession()) {
      return;
    }

    const user =
      assignedEntity ||
      activeContact ||
      (session.remoteUser.connectedUsers[0] as IEntity);
    const { isDialerActive } = State.state$.getValue();

    const callType = isDialerActive
      ? ESolgariCallType.DIALER
      : ESolgariCallType.REGULAR;

    const { id } = await Dynamics.savePhoneCallNote(
      {
        desc: text,
        entity: user,
        timer: session.timer,
        noteId,
        relatedInfo: relatedToData,
        callState: EStatusCode.OPEN,
        callType,
        ...(callNote?.extendedSubject && {
          extendedSubject: callNote.extendedSubject,
        }),
      },
      session,
    );
    return id;
  };

  useEffect(() => {
    const sub$ = SipJs.sessions$.subscribe((sessions: ISession[]) => {
      setSessions(
        sessions.sort((a: ISession, b: ISession): number => {
          return +a.primalSession.startTime - +b.primalSession.startTime;
        }),
      );
    });

    return () => sub$.unsubscribe();
  }, []);

  // TODO rework this as soon as possible, move that logic into usePhoneCalls; rework ESessionStatus events completely, move call note object into local storage or mobx store
  useEffect(() => {
    if (sessions.length === 0) return;

    const session = sessions[0];
    if (!session?.sessionState$) return;

    const sub$ = session.sessionState$.subscribe(
      async (status: ESessionStatus) => {
        if (status === ESessionStatus.ACCEPTED && !callNote.id) {
          const noteId = await savePhoneCall(callNote, session);
          setCallNote({ ...callNote, id: noteId });
          State.setState({ callNoteId: noteId });
        }

        if (status === ESessionStatus.ACCEPTED && SipJs.conference) {
          setWrapUpSession(session);
        }

        if (
          status === ESessionStatus.ENDED ||
          status === ESessionStatus.COMPLETED
        ) {
          const { wrapUpQueue } = State.state$.getValue();
          if (Dynamics.getVersion() === 2 && !wrapUpQueue?.isWorking) {
            State.setState({ callNoteId: null });
          }
        }
      },
    );

    return () => sub$.unsubscribe();
  }, [sessions]);

  return (
    <div className="active-page">
      {sessions.length > 0 && renderPlayers()}

      {wrapUp?.show && <WrapUp wrapUp={wrapUp} removeWrapUp={removeWrapUp} />}

      {renderSubPanel(subPanel)}

      {isMultiSession() && (
        <div className="btn-wrapper">
          <Button
            data-testid="attended-transfer"
            type="primary"
            onClick={() => attendedTransfer()}
            icon={<MessageIcon svgUrl={transferIcon} />}
          >
            Attended Transfer
          </Button>
        </div>
      )}
    </div>
  );
});
